/**
 * Phase 23 — Admin Partner Service-Level Expectations.
 *
 * Route: /admin/partners/service-levels
 *
 * PINNED USER RULE (Feb 2026):
 *   Internal admin-only visibility. Service-level expectations are
 *   NOT used to automatically change partner status / preferred flag /
 *   commission / payouts / pricing / allocations. No emails, SMS,
 *   social posts, public pages, or partner / customer notifications
 *   are sent. The wording on the screen avoids punitive language
 *   ("breach", "penalty", "strike") and uses operational wording
 *   instead ("follow-up due", "at risk", "overdue").
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft, AlertTriangle, Loader2, ShieldAlert, Save, Search,
  History, Clock, Filter, RefreshCw, Activity,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { PortalShell } from "@/components/PortalShell";


const ITEM_STATUSES = ["on_track", "at_risk", "overdue", "complete", "not_applicable"];


const STATUS_COLORS = {
  on_track:        "bg-emerald-100 text-emerald-800 border-emerald-200",
  at_risk:         "bg-amber-100 text-amber-800 border-amber-200",
  overdue:         "bg-rose-100 text-rose-800 border-rose-200",
  complete:        "bg-slate-100 text-slate-600 border-slate-200",
  not_applicable:  "bg-slate-50 text-slate-400 border-slate-200",
};


function StatusBadge({ status }) {
  const cls = STATUS_COLORS[status] || STATUS_COLORS.not_applicable;
  return (
    <span
      data-testid={`ph23-status-${status}`}
      className={`inline-flex items-center text-[10px] font-semibold px-2 py-0.5 rounded-full border ${cls}`}
    >
      {status.replace("_", " ")}
    </span>
  );
}


// ── Partner picker ──────────────────────────────────────────────────────
function PartnerPicker({ partners, partnerId, onChange }) {
  const [query, setQuery] = useState("");
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return partners.slice(0, 50);
    return partners.filter((p) =>
      (p.business_name || "").toLowerCase().includes(q) ||
      (p.partner_slug || "").toLowerCase().includes(q) ||
      (p.franchise_region || "").toLowerCase().includes(q),
    ).slice(0, 50);
  }, [partners, query]);
  return (
    <section
      data-testid="ph23-partner-picker"
      className="rounded-2xl bg-white border border-slate-200 p-4"
    >
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.15em] font-bold text-slate-700 mb-2">
        <Search className="w-3.5 h-3.5 text-cyan-700" /> Select PWS partner
      </div>
      <input
        data-testid="ph23-partner-search"
        type="search"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search by name, slug or region…"
        className="text-sm w-full border border-slate-200 rounded-md px-2 py-2 mb-2"
      />
      <div className="max-h-64 overflow-y-auto divide-y divide-slate-100">
        {filtered.map((p) => (
          <button
            key={p.id}
            type="button"
            data-testid={`ph23-partner-option-${p.id}`}
            onClick={() => onChange(p.id)}
            className={`w-full text-left px-2 py-1.5 hover:bg-slate-50 ${
              partnerId === p.id ? "bg-cyan-50 border-l-2 border-cyan-500" : ""
            }`}
          >
            <div className="font-semibold text-slate-800 text-xs">
              {p.business_name || "—"}{" "}
              <span className="text-[10px] text-slate-500 font-mono">
                {p.partner_slug || ""}
              </span>
            </div>
            <div className="text-[10px] text-slate-500">
              {p.franchise_region || "—"} · {p.pws_partner_status}
            </div>
          </button>
        ))}
        {filtered.length === 0 && (
          <div className="text-xs text-slate-400 italic px-2 py-3">
            No partners match.
          </div>
        )}
      </div>
    </section>
  );
}


// ── Settings form ──────────────────────────────────────────────────────
const HOUR_FIELDS = [
  { key: "expected_response_hours",            label: "Expected response time",            hint: "Hours from booking allocation to first partner response." },
  { key: "expected_follow_up_hours",           label: "Expected follow-up time",           hint: "Hours from follow-up creation to partner action." },
  { key: "expected_opportunity_review_hours",  label: "Expected opportunity review time",  hint: "Hours from opportunity creation to admin review." },
  { key: "expected_customer_contact_hours",    label: "Expected customer contact time",    hint: "Hours from workflow creation to customer / partner contact." },
  { key: "expected_quote_follow_up_hours",     label: "Expected quote follow-up time",     hint: "Hours from quote_requested / quoted status to next action." },
];


function HourInput({ value, onChange, testId }) {
  return (
    <input
      type="number"
      min={1}
      max={720}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))}
      data-testid={testId}
      className="text-sm w-24 border border-slate-200 rounded-md px-2 py-1.5 text-right font-mono"
    />
  );
}


function SettingsForm({ body, onSave, saving }) {
  const [draft, setDraft] = useState(body.settings);
  const [reason, setReason] = useState("");

  useEffect(() => {
    setDraft(body.settings);
    setReason("");
  }, [body]);

  const set = useCallback((k, v) => setDraft((d) => ({ ...d, [k]: v })), []);

  const dirty = useMemo(() => {
    return Object.keys(draft).some((k) => draft[k] !== body.settings[k]);
  }, [draft, body.settings]);

  const onSubmit = useCallback(async (e) => {
    e?.preventDefault?.();
    if (!dirty) { toast.error("No changes to save"); return; }
    const patch = {};
    for (const f of [...HOUR_FIELDS.map((h) => h.key), "service_level_enabled", "service_level_note"]) {
      if (draft[f] !== body.settings[f]) patch[f] = draft[f];
    }
    if (reason.trim()) patch.reason = reason.trim();
    await onSave(patch);
  }, [dirty, draft, body.settings, reason, onSave]);

  return (
    <form
      data-testid="ph23-settings-form"
      onSubmit={onSubmit}
      className="rounded-2xl bg-white border border-slate-200 p-5"
    >
      <div className="text-[11px] uppercase tracking-[0.15em] font-bold text-slate-700 mb-3 flex items-center gap-1.5">
        <Clock className="w-3.5 h-3.5 text-cyan-700" /> Service-level expectations
      </div>

      {/* Enabled toggle */}
      <div className="flex items-start gap-3 py-2 border-b border-slate-100">
        <button
          type="button"
          data-testid="ph23-toggle-service_level_enabled"
          onClick={() => set("service_level_enabled", !draft.service_level_enabled)}
          className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
            draft.service_level_enabled ? "bg-emerald-500" : "bg-slate-300"
          }`}
          role="switch"
          aria-checked={!!draft.service_level_enabled}
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
              draft.service_level_enabled ? "translate-x-4" : "translate-x-0.5"
            }`}
          />
        </button>
        <div className="flex-1">
          <div className="text-sm font-semibold text-slate-800">
            Service-level tracking enabled
          </div>
          {!draft.service_level_enabled && (
            <div className="text-[10px] text-amber-700 mt-0.5 flex items-start gap-1">
              <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" />
              <span>
                When OFF, this partner&apos;s items appear as
                <code className="font-mono mx-1">not_applicable</code> in
                the monitor. They are not classified as overdue or at-risk.
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Hour fields */}
      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
        {HOUR_FIELDS.map((f) => (
          <div key={f.key} className="border border-slate-100 rounded-md p-3">
            <div className="flex items-center justify-between gap-2">
              <label className="text-xs font-semibold text-slate-700">
                {f.label}
              </label>
              <HourInput
                testId={`ph23-input-${f.key}`}
                value={draft[f.key]}
                onChange={(v) => set(f.key, v)}
              />
            </div>
            <div className="text-[10px] text-slate-500 mt-1">
              {f.hint} <span className="font-mono">hours</span>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-3">
        <label className="text-xs font-semibold text-slate-700 block mb-1">
          Internal note (admin-only)
        </label>
        <textarea
          rows={2}
          maxLength={1000}
          value={draft.service_level_note || ""}
          onChange={(e) => set("service_level_note", e.target.value)}
          data-testid="ph23-input-service_level_note"
          className="text-sm w-full border border-slate-200 rounded-md px-2 py-2"
          placeholder="Context — capacity, leave, contractual SLA notes etc."
        />
      </div>

      <div className="mt-4 border-t border-slate-100 pt-3">
        <label className="text-xs font-semibold text-slate-700 block mb-1">
          Reason for this change (recorded in audit log)
        </label>
        <textarea
          rows={2}
          maxLength={1000}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          data-testid="ph23-input-reason"
          className="text-sm w-full border border-slate-200 rounded-md px-2 py-2"
          placeholder="e.g. relaxing follow-up window during partner onboarding"
        />
      </div>

      <div className="mt-4 flex items-center justify-between gap-2 flex-wrap">
        <div className="text-[10px] text-slate-400 italic">
          Saved settings write a row to{" "}
          <code className="font-mono">pws_partner_service_level_audit</code>.
        </div>
        <button
          type="submit"
          disabled={!dirty || saving}
          data-testid="ph23-save-btn"
          className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold transition-colors ${
            dirty && !saving
              ? "bg-cyan-700 text-white hover:bg-cyan-800"
              : "bg-slate-200 text-slate-500 cursor-not-allowed"
          }`}
        >
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
          {saving ? "Saving…" : "Save settings"}
        </button>
      </div>
    </form>
  );
}


// ── Audit list ──────────────────────────────────────────────────────────
function AuditList({ rows }) {
  return (
    <section
      data-testid="ph23-audit-list"
      className="rounded-2xl bg-white border border-slate-200 p-5"
    >
      <div className="text-[11px] uppercase tracking-[0.15em] font-bold text-slate-700 mb-2 flex items-center gap-1.5">
        <History className="w-3.5 h-3.5 text-slate-700" /> Recent service-level audit
      </div>
      {rows.length === 0 && (
        <div className="text-xs text-slate-400 italic">No audit entries yet.</div>
      )}
      <ul className="divide-y divide-slate-100">
        {rows.map((r, i) => (
          <li key={i} data-testid={`ph23-audit-row-${i}`} className="py-2 text-xs">
            <div className="font-semibold text-slate-800">
              {r.changed_at.slice(0, 19).replace("T", " ")} ·{" "}
              <span className="text-slate-500 font-normal">{r.changed_by}</span>
            </div>
            <div className="mt-0.5 text-[10px] text-slate-500">
              Source: <span className="font-mono">{r.source}</span>
              {r.reason && (<> · Reason: <span className="text-slate-700">{r.reason}</span></>)}
            </div>
            <ul className="mt-1 space-y-0.5">
              {Object.entries(r.changes || {}).map(([f, c]) => (
                <li key={f} className="text-[11px] text-slate-700">
                  <span className="font-semibold">{f}</span>:{" "}
                  <span className="line-through text-slate-400">{JSON.stringify(c.from)}</span>
                  {" "}→ <span className="text-emerald-700">{JSON.stringify(c.to)}</span>
                </li>
              ))}
            </ul>
          </li>
        ))}
      </ul>
    </section>
  );
}


// ── Monitor table ──────────────────────────────────────────────────────
function MonitorTable({ data, statusFilter, onStatusFilter }) {
  return (
    <section
      data-testid="ph23-monitor"
      className="rounded-2xl bg-white border border-slate-200 p-5"
    >
      <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
        <div className="text-[11px] uppercase tracking-[0.15em] font-bold text-slate-700 flex items-center gap-1.5">
          <Activity className="w-3.5 h-3.5 text-cyan-700" /> Service-level monitor
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          <Filter className="w-3 h-3 text-slate-500" />
          <button
            type="button"
            data-testid="ph23-filter-all"
            onClick={() => onStatusFilter(null)}
            className={`text-[10px] font-semibold px-2 py-1 rounded-full border ${
              !statusFilter
                ? "bg-cyan-700 text-white border-cyan-700"
                : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
            }`}
          >
            all
          </button>
          {ITEM_STATUSES.map((s) => (
            <button
              key={s}
              type="button"
              data-testid={`ph23-filter-${s}`}
              onClick={() => onStatusFilter(s)}
              className={`text-[10px] font-semibold px-2 py-1 rounded-full border ${
                statusFilter === s
                  ? "bg-cyan-700 text-white border-cyan-700"
                  : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
              }`}
            >
              {s.replace("_", " ")} ({data?.totals?.[s] ?? 0})
            </button>
          ))}
        </div>
      </div>

      {!data || data.items.length === 0 ? (
        <div
          data-testid="ph23-monitor-empty"
          className="text-xs text-slate-400 italic py-6 text-center"
        >
          No service-level items match the current filter.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-2 py-1.5 text-left">Status</th>
                <th className="px-2 py-1.5 text-left">Partner</th>
                <th className="px-2 py-1.5 text-left">Kind</th>
                <th className="px-2 py-1.5 text-left">Item</th>
                <th className="px-2 py-1.5 text-left">Expected action</th>
                <th className="px-2 py-1.5 text-right">Expected hrs</th>
                <th className="px-2 py-1.5 text-right">Age hrs</th>
                <th className="px-2 py-1.5 text-right">Remaining hrs</th>
                <th className="px-2 py-1.5"></th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((it, idx) => (
                <tr
                  key={`${it.kind}-${it.item_id || idx}`}
                  data-testid={`ph23-item-${it.kind}-${idx}`}
                  className="border-t border-slate-100 hover:bg-slate-50"
                >
                  <td className="px-2 py-1.5"><StatusBadge status={it.status} /></td>
                  <td className="px-2 py-1.5">
                    <div className="font-semibold text-slate-800">{it.business_name || "—"}</div>
                    <div className="text-[10px] text-slate-500 font-mono">{it.partner_slug || ""}</div>
                  </td>
                  <td className="px-2 py-1.5 text-slate-700">{it.kind}</td>
                  <td className="px-2 py-1.5">
                    <div className="text-slate-700">{it.category_label || it.category_slug || "—"}</div>
                    <div className="text-[10px] text-slate-500 font-mono">{it.booking_reference || ""}</div>
                  </td>
                  <td className="px-2 py-1.5 text-slate-700">{it.expected_action}</td>
                  <td className="px-2 py-1.5 text-right font-mono">{it.expected_hours}</td>
                  <td className="px-2 py-1.5 text-right font-mono">{it.age_hours ?? "—"}</td>
                  <td className="px-2 py-1.5 text-right font-mono">{it.remaining_hours ?? "—"}</td>
                  <td className="px-2 py-1.5 text-right">
                    <Link
                      to={it.deep_link}
                      data-testid={`ph23-item-deep-link-${it.kind}-${idx}`}
                      className="text-[10px] font-semibold text-blue-700 hover:text-blue-800"
                    >
                      Open
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}


// ── Page ────────────────────────────────────────────────────────────────
export default function AdminPWSPartnerServiceLevels() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [partners, setPartners] = useState([]);
  const [partnerId, setPartnerId] = useState(null);
  const [body, setBody] = useState(null);
  const [bodyLoading, setBodyLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [monitor, setMonitor] = useState(null);
  const [statusFilter, setStatusFilter] = useState(null);
  const [aggregate, setAggregate] = useState(null);

  // Load partner list + aggregate monitor on mount.
  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const [{ data: p }, { data: m }] = await Promise.all([
          api.get("/admin/pws-partners?limit=500"),
          api.get("/admin/pws-partner-service-levels-monitor"),
        ]);
        if (!alive) return;
        setPartners((p.items || []).filter((x) => x.partner_type === "pws_franchisee"));
        setAggregate(m);
      } catch (e) {
        if (alive) setError(formatApiError(e));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  // Fetch settings + monitor when partner changes.
  useEffect(() => {
    setBody(null);
    setMonitor(null);
    if (!partnerId) return;
    let alive = true;
    (async () => {
      setBodyLoading(true);
      try {
        const [{ data: b }, { data: m }] = await Promise.all([
          api.get(`/admin/pws-partner-service-levels/${partnerId}`),
          api.get(`/admin/pws-partner-service-levels/${partnerId}/monitor`),
        ]);
        if (!alive) return;
        setBody(b); setMonitor(m);
      } catch (e) {
        if (alive) toast.error(formatApiError(e) || "Failed to load");
      } finally {
        if (alive) setBodyLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [partnerId]);

  // Re-fetch monitor when status filter changes.
  useEffect(() => {
    if (!partnerId) {
      // Aggregate filter.
      let alive = true;
      (async () => {
        try {
          const url = statusFilter
            ? `/admin/pws-partner-service-levels-monitor?status=${statusFilter}`
            : `/admin/pws-partner-service-levels-monitor`;
          const { data } = await api.get(url);
          if (alive) setAggregate(data);
        } catch (e) { /* swallow */ }
      })();
      return () => { alive = false; };
    }
    let alive = true;
    (async () => {
      try {
        const url = statusFilter
          ? `/admin/pws-partner-service-levels/${partnerId}/monitor?status=${statusFilter}`
          : `/admin/pws-partner-service-levels/${partnerId}/monitor`;
        const { data } = await api.get(url);
        if (alive) setMonitor(data);
      } catch (e) { /* swallow */ }
    })();
    return () => { alive = false; };
  }, [statusFilter, partnerId]);

  const onSave = useCallback(async (patch) => {
    if (!partnerId) return;
    setSaving(true);
    try {
      await api.patch(`/admin/pws-partner-service-levels/${partnerId}`, patch);
      const { data: refreshed } = await api.get(`/admin/pws-partner-service-levels/${partnerId}`);
      setBody(refreshed);
      const changes = Object.keys(refreshed.changes || {});
      toast.success("Settings saved");
    } catch (e) {
      toast.error(formatApiError(e) || "Failed to save");
    } finally {
      setSaving(false);
    }
  }, [partnerId]);

  const monitorData = partnerId ? monitor : aggregate;

  return (
    <PortalShell>
      <div
        data-testid="admin-pws-partner-service-levels-page"
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8"
      >
        <Link
          to="/admin/partners/network"
          data-testid="ph23-back-to-network"
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-blue-700 hover:text-blue-800"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Back to PWS Network
        </Link>

        <div className="mt-3 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-cyan-700">
              Aware Partner Network · Phase 23
            </div>
            <h1 className="font-display mt-2 text-3xl font-bold text-slate-900">
              Partner Service-Level Expectations
            </h1>
            <p className="text-slate-500 mt-1 text-sm max-w-3xl">
              Internal admin indicator only. Set hour-based expectations
              for partner response, follow-up, opportunity review,
              customer contact and quote follow-up. The monitor shows
              which items are on-track, at-risk, overdue, complete or
              not-applicable.
            </p>
          </div>
        </div>

        <div
          data-testid="ph23-safety-banner"
          className="mt-5 rounded-xl bg-cyan-50 border border-cyan-200 p-4 flex items-start gap-3 text-sm text-cyan-900"
        >
          <ShieldAlert className="w-5 h-5 mt-0.5 flex-shrink-0" />
          <div>
            <strong>Admin-only visibility.</strong> Service-level
            indicators DO NOT automatically change partner status,
            preferred-partner flag, public-lead allocation, region
            allocation, commission, pricing, payouts or white-label.
            No emails / SMS / partner / customer notifications are
            sent. No ServiceM8 jobs / quotes / proposals are created.
            Wording is operational and never punitive.
          </div>
        </div>

        {loading ? (
          <div
            data-testid="ph23-loading"
            className="mt-8 rounded-2xl bg-white border border-slate-200 p-10 flex items-center gap-2 text-slate-400 justify-center"
          >
            <Loader2 className="w-5 h-5 animate-spin" /> Loading…
          </div>
        ) : error ? (
          <div
            data-testid="ph23-error"
            className="mt-8 rounded-xl bg-rose-50 border border-rose-200 p-5 text-sm text-rose-800"
          >
            <div className="font-semibold mb-1 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" /> Failed to load
            </div>
            <div className="text-xs">{error}</div>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-4 mt-4">
            <PartnerPicker partners={partners} partnerId={partnerId} onChange={setPartnerId} />
            <div className="space-y-4">
              {!partnerId && (
                <div
                  data-testid="ph23-no-partner-selected"
                  className="rounded-2xl bg-white border border-dashed border-slate-200 p-6 text-center text-slate-500 text-sm"
                >
                  Pick a partner to view & edit their service-level
                  expectations, or browse the aggregate monitor below.
                </div>
              )}
              {partnerId && bodyLoading && (
                <div
                  data-testid="ph23-body-loading"
                  className="rounded-2xl bg-white border border-slate-200 p-10 flex items-center gap-2 text-slate-400 justify-center"
                >
                  <Loader2 className="w-5 h-5 animate-spin" /> Loading…
                </div>
              )}
              {partnerId && !bodyLoading && body && (
                <>
                  <section
                    data-testid="ph23-partner-header"
                    className="rounded-2xl bg-white border border-slate-200 p-4"
                  >
                    <div className="font-bold text-slate-800">
                      {body.business_name || "—"}
                    </div>
                    <div className="text-xs text-slate-500 font-mono">
                      {body.partner_slug || "—"} · {body.franchise_region || "—"} · {body.state || "—"} · status: {body.partner_status}
                    </div>
                    {body.settings.service_level_updated_at && (
                      <div className="text-[10px] text-slate-500 italic mt-1">
                        Last changed: {body.settings.service_level_updated_at.slice(0, 19).replace("T", " ")} by {body.settings.service_level_updated_by || "—"}
                      </div>
                    )}
                  </section>
                  <SettingsForm body={body} onSave={onSave} saving={saving} />
                  <AuditList rows={body.recent_audit || []} />
                </>
              )}
              <MonitorTable
                data={monitorData}
                statusFilter={statusFilter}
                onStatusFilter={setStatusFilter}
              />
            </div>
          </div>
        )}
      </div>
    </PortalShell>
  );
}
